import { z } from 'zod';

import { IT_VERTICALS } from '../constants/verticals';
import { cuidLike, isoLanguageCode } from '../zod-common';

import { BIDDER_PREFERENCES, OFFER_MODES } from './offer';

export const SENIORITY_LEVELS = ['SENIOR', 'JUNIOR', 'ANY'] as const;
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

export const FEED_PREQUALIF_OPTIONS = ['ALL', 'GREEN_YELLOW', 'GREEN'] as const;
export type FeedPrequalifOption = (typeof FEED_PREQUALIF_OPTIONS)[number];

export const FRESHNESS_WINDOWS = ['LAST_24H', 'LAST_WEEK', 'LAST_30D'] as const;
export type FreshnessWindow = (typeof FRESHNESS_WINDOWS)[number];

export const FEED_BUDGET_BASELINE_EUR = 600;
export const FEED_BUDGET_DAYS_PER_PARENT = 5;
export const FEED_CANDIDATE_FETCH_LIMIT = 200;
export const FEED_DEFAULT_LIMIT = 20;
export const FEED_MAX_LIMIT = 50;

const optionalNonEmptyKeywords = z
  .string()
  .trim()
  .min(1, 'Mot-clé trop court')
  .max(120, 'Mot-clé trop long')
  .optional();

export const feedFiltersSchema = z
  .object({
    verticals: z.array(z.enum(IT_VERTICALS)).max(IT_VERTICALS.length).optional(),
    languages: z.array(isoLanguageCode).max(20).optional(),
    budget: z
      .object({
        min: z.number().nonnegative('Min ≥ 0'),
        max: z.number().nonnegative('Max ≥ 0'),
      })
      .refine((v) => v.max >= v.min, 'Max doit être ≥ Min')
      .optional(),
    seniority: z.enum(SENIORITY_LEVELS).optional(),
    bidderPreference: z.enum(BIDDER_PREFERENCES).optional(),
    mode: z.array(z.enum(OFFER_MODES)).max(OFFER_MODES.length).optional(),
    freshness: z.enum(FRESHNESS_WINDOWS).optional(),
    keywords: optionalNonEmptyKeywords,
    prequalification: z.enum(FEED_PREQUALIF_OPTIONS).optional(),
    minMatchScore: z.number().int().min(0).max(100).optional(),
  })
  .default({});

export type FeedFilters = z.infer<typeof feedFiltersSchema>;

export const FEED_SORT_OPTIONS = ['MATCH_SCORE', 'DATE', 'BUDGET'] as const;
export type FeedSortBy = (typeof FEED_SORT_OPTIONS)[number];

export const feedListInputSchema = z.object({
  filters: feedFiltersSchema.optional(),
  sortBy: z.enum(FEED_SORT_OPTIONS).default('MATCH_SCORE'),
  cursor: z.string().max(2048).optional(),
  limit: z.number().int().min(1).max(FEED_MAX_LIMIT).default(FEED_DEFAULT_LIMIT),
});
export type FeedListInput = z.infer<typeof feedListInputSchema>;

export const feedDismissSchema = z.object({
  offerId: cuidLike,
});
export type FeedDismissInput = z.infer<typeof feedDismissSchema>;

export const feedUndismissSchema = z.object({
  offerId: cuidLike,
});
export type FeedUndismissInput = z.infer<typeof feedUndismissSchema>;
